// Purpose: Dockable bounty component to attach inside UniversalEvidenceWindow
// Audience: Evidence modal viewers who want to submit/track bounties without a separate popup
// Features:
// - Three tabs: Submit, View Bounties, Results
// - Uses new BountyConfigurationMenu for professional configuration interface

import React, { useState, useEffect } from 'react';
import { FaDollarSign, FaList, FaChartBar } from 'react-icons/fa';
import { BiTargetLock } from 'react-icons/bi';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import { BountyResultsModal } from '../modals/results/wrappers/CleanResultsModals';
import BountyConfigurationMenu from './BountyConfigurationMenu';
import '../../styles/BountyModal.css';
import '../../styles/EvidenceModal.css';
import { useNotifications } from '../notifications/NotificationsProvider';

const BountyComponent = ({ evidence, currentUser, casefileId, casefileName, initialTab = 'submit', navigationTrigger }) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const { notify } = useNotifications();
  const [existingBounties, setExistingBounties] = useState([]);

  // Update active tab when initialTab prop changes (for direct navigation)
  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab, navigationTrigger]);
  const [bountyResults, setBountyResults] = useState([]);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [selectedResultOrderId, setSelectedResultOrderId] = useState(null);
  const [showConfigMenu, setShowConfigMenu] = useState(false);

  // Results stream for this evidence (bounty fulfillments)
  useEffect(() => {
    if (!evidence?.id) return;
    
    // Build a query without orderBy to avoid composite-index requirements in public contexts
    const baseQuery = query(
      collection(db, 'orders'),
      where('evidenceIds', 'array-contains', evidence.id),
      where('orderType', '==', 'bounty_fulfillment')
    );

    let unsubscribe = () => {};
    try {
      unsubscribe = onSnapshot(
        baseQuery,
        (snapshot) => {
          const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          // Client-side sort by createdAt if present
          items.sort((a, b) => {
            const aTs = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
            const bTs = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
            return bTs - aTs;
          });
          setBountyResults(items);
        },
        (error) => {
          console.error('Bounty results listener error:', error);
          // Gracefully degrade: clear results but keep UI functional
          setBountyResults([]);
          notify({ 
            severity: 'warning', 
            title: 'Results Unavailable', 
            message: 'Could not load bounty results live. They may require additional permissions or indexes.' 
          });
        }
      );
    } catch (err) {
      console.error('Failed to attach bounty results listener:', err);
    }

    return () => { 
      try { 
        unsubscribe(); 
      } catch (_) {} 
    };
  }, [evidence?.id, notify]);

  // Mock existing bounties (replace with Firestore collection later)
  useEffect(() => {
    if (evidence) {
      setExistingBounties([
        { 
          id: 'bounty1', 
          createdBy: 'investigator@example.com', 
          createdByName: 'Detective Smith', 
          amount: 25.00, 
          targets: ['vehicles', 'people'], 
          description: 'Need clear license plate reading and driver identification', 
          deadline: '2025-01-20', 
          status: 'open', 
          responses: 2 
        },
        { 
          id: 'bounty2', 
          createdBy: 'analyst@example.com', 
          createdByName: 'Crime Analyst Johnson', 
          amount: 15.00, 
          targets: ['activities'], 
          description: 'Timeline reconstruction of the incident sequence', 
          deadline: '2025-01-18', 
          status: 'open', 
          responses: 0 
        }
      ]);
    }
  }, [evidence]);

  const handleRespondToBounty = (bountyId) => {
    const bounty = existingBounties.find(b => b.id === bountyId);
    if (!bounty) return;
    
    console.log('Creating bounty fulfillment order:', {
      evidenceId: evidence.id,
      casefileId,
      orderType: 'bounty_fulfillment',
      bountyContext: {
        bountyId: bountyId,
        bountyCreator: bounty.createdBy,
        bountyAmount: bounty.amount,
        bountyDescription: bounty.description,
        isCompleted: false,
        completedAt: null,
        paymentStatus: 'pending'
      },
      analysisRequest: {
        scanTier: 'configured_scan',
        targets: bounty.targets,
        selectedModel: 'gemini-2.5-flash',
        preferredService: 'google-vertex-vision-standard'
      },
      requestedBy: currentUser?.email,
      orderName: `Bounty Fulfillment - ${bounty.description.substring(0, 50)}...`
    });
    
    notify({ 
      severity: 'success', 
      title: 'Bounty Analysis Started', 
      message: 'Results will appear in the Bounty Results tab.' 
    });
  };

  const handleViewResults = (orderId) => { 
    setSelectedResultOrderId(orderId); 
    setShowResultsModal(true); 
  };
  
  const handleViewAllResults = () => { 
    setSelectedResultOrderId(null); 
    setShowResultsModal(true); 
  };

  const handleBountyCreated = (bountyData) => {
    notify({ 
      severity: 'success', 
      title: 'Bounty Created', 
      message: `Your bounty has been posted successfully.` 
    });
    setActiveTab('bounties');
    setShowConfigMenu(false);
  };

  if (!evidence) return null;

  return (
    <div className="bounty-tab-content" style={{ marginTop: 0 }}>
      <div className="analysis-header-with-tabs">
        <h4 style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '8px', 
          marginTop: 0, 
          marginBottom: '15px', 
          color: '#fff' 
        }}>
          <BiTargetLock style={{ color: '#ffc107' }} />
          Request Analysis
        </h4>
        <div className="analysis-tabs">
          <button 
            className={`analysis-tab ${activeTab === 'submit' ? 'active' : ''}`}
            onClick={() => setActiveTab('submit')}
          >
            <FaDollarSign />
            Submit Bounty
          </button>
          <button 
            className={`analysis-tab ${activeTab === 'bounties' ? 'active' : ''}`}
            onClick={() => setActiveTab('bounties')}
          >
            <FaList />
            View Bounties
            {existingBounties.length > 0 && (
              <span className="analysis-count-badge">{existingBounties.length}</span>
            )}
          </button>
          <button 
            className={`analysis-tab ${activeTab === 'results' ? 'active' : ''}`}
            onClick={() => setActiveTab('results')}
          >
            <FaChartBar />
            Bounty Results
            {bountyResults.length > 0 && (
              <span className="analysis-count-badge">{bountyResults.length}</span>
            )}
          </button>
        </div>
      </div>

      {activeTab === 'submit' && (
        <div>
          {!showConfigMenu ? (
            <div className="bounty-cta-panel">
              {/* Row 1: Target left, Title right on same line */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <BiTargetLock className="bounty-cta-robot" style={{ marginBottom: 0, color: '#ffc107', fontSize: '4rem' }} />
                <h3 style={{ margin: 0, fontWeight: 700 }}>Professional Bounty Request</h3>
              </div>
              {/* Row 2: Centered subtitle */}
              <div style={{ textAlign: 'center', marginBottom: 16, opacity: 0.9, fontSize: '1rem', color: '#cbd5e1' }}>
                Configure a bounty request for analysis
              </div>
              {/* Row 3: Buttons centered with space between */}
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12 }}>
                <button 
                  onClick={() => setShowConfigMenu(true)}
                  className="bounty-cta-btn bounty-cta-primary"
                >
                  Configure
                </button>
                {/* Quick Bounty Button */}
                <button
                  onClick={() => {
                    // Quick bounty creation with defaults
                    console.log('Quick bounty creation not implemented yet');
                    notify({
                      severity: 'info',
                      title: 'Quick Bounty',
                      message: 'Quick bounty feature coming soon. Please use Configure.'
                    });
                  }}
                  className="bounty-cta-btn bounty-cta-secondary"
                  style={{ padding: '10px 16px', fontSize: '0.9rem' }}
                >
                  Quick Bounty →
                </button>
              </div>
            </div>
          ) : (
            <BountyConfigurationMenu
              evidence={evidence}
              casefileId={casefileId}
              onBountyCreated={handleBountyCreated}
              onClose={() => setShowConfigMenu(false)}
              currentUser={currentUser}
            />
          )}
        </div>
      )}

      {activeTab === 'bounties' && (
        <div className="bounties-list-tab">
          <h4>Available Bounties</h4>
          {existingBounties.length === 0 ? (
            <div className="no-bounties">
              <FaDollarSign className="empty-icon" />
              <p>No bounties available for this evidence.</p>
              <p>Be the first to create a bounty request!</p>
            </div>
          ) : (
            <div className="bounties-list">
              {existingBounties.map(bounty => (
                <div key={bounty.id} className="bounty-card">
                  <div className="bounty-header">
                    <div className="bounty-info">
                      <h5>{bounty.description}</h5>
                      <div className="bounty-meta">
                        <span>By: {bounty.createdByName}</span>
                        <span>Deadline: {bounty.deadline}</span>
                        <span>{bounty.responses} responses</span>
                      </div>
                    </div>
                    <div className="bounty-amount">${bounty.amount.toFixed(2)}</div>
                  </div>
                  <div className="bounty-targets">
                    {bounty.targets.map(target => (
                      <span key={target} className="target-tag">{target}</span>
                    ))}
                  </div>
                  <button 
                    className="respond-btn" 
                    onClick={() => handleRespondToBounty(bounty.id)}
                  >
                    Respond to Bounty
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'results' && (
        <div className="bounty-results-tab">
          <div className="results-header">
            <h4>Bounty Results</h4>
            {bountyResults.length > 0 && (
              <button className="view-all-results-btn" onClick={handleViewAllResults}>
                View All Results
              </button>
            )}
          </div>
          {bountyResults.length === 0 ? (
            <div className="no-results">
              <FaChartBar className="empty-icon" />
              <p>No bounty results yet.</p>
              <p>Results will appear here when bounties are completed.</p>
            </div>
          ) : (
            <div className="bounty-results-list">
              {bountyResults.slice(0, 5).map(result => (
                <div key={result.id} className="bounty-result-card">
                  <div className="result-header">
                    <div className="result-info">
                      <h5>Bounty Analysis Result</h5>
                      <div className="result-meta">
                        <span>Order ID: {result.id}</span>
                        <span>Status: {result.status || 'Processing'}</span>
                      </div>
                    </div>
                    <div className="result-amount">${result.bountyContext?.bountyAmount || 0}</div>
                  </div>
                  <button 
                    className="view-result-btn" 
                    onClick={() => handleViewResults(result.id)}
                  >
                    View Results
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Bounty Results Modal */}
      {showResultsModal && (
        <BountyResultsModal
          isOpen={showResultsModal}
          onClose={() => {
            setShowResultsModal(false);
            setSelectedResultOrderId(null);
          }}
          evidenceId={evidence.id}
          evidenceName={evidence.fileName}
          casefileId={casefileId}
          casefileName={casefileName}
          currentUser={currentUser}
          orderId={selectedResultOrderId}
        />
      )}
    </div>
  );
};

export default BountyComponent;